import { api, type SharedFolderDetail } from '../api/client';
import { uploadErrorMessageKey } from '../api/uploadErrors';
import { getAppState } from '../app/index';
import { listDeletedSharedFolderFiles, renameSharedFolderFile } from '../crypto/sharedFolderDeletion';
import {
  downloadSharedFolderFile,
  formatFileSize,
  replaceSharedFolderFileContents,
  triggerBrowserDownload,
  uploadFileToSharedFolder,
} from '../crypto/sharedFolderFile';
import { unwrapSharedFolderKey } from '../crypto/sharedFolderKey';
import { resolveFolderShareLabel } from '../crypto/shareLabelUpload';
import {
  createSharedFolderSubfolder,
  deleteSharedFolderFile,
  deleteSharedFolderSubfolder,
  moveSharedFolderFile,
  moveSharedFolderSubfolder,
  renameSharedFolderSubfolder,
  summarizeDirectoryDelete,
} from '../crypto/sharedFolderSubfolder';
import { loadVaultSession } from '../crypto/vaultSession';
import {
  isDirectoryEmpty,
  joinManifestPath,
  isManifestPathTaken,
  listFolderContents,
  loadSharedFolderManifest,
  parentManifestPath,
  type SharedFolderActorRef,
  type SharedFolderDirectoryEntry,
  type SharedFolderFileEntry,
  type SharedFolderManifestPlaintext,
} from '../crypto/sharedFolderManifest';
import { canEditPlainText, ensureTextFileExtension, fileTypeIconFor, fileTypeLabelKeyFor, inferContentTypeFromFileName, resolveFilePreview } from '../files/fileTypes';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { tInfoShareStatus } from '../i18n/status';
import { enhanceDataTableCards } from '../ui/dataTableCards';
import { renderFilesBreadcrumb } from '../ui/filesBreadcrumb';
import { closeFilesContextMenu, openFilesContextMenu } from '../ui/filesContextMenu';
import { ensureExpandedAncestors, renderFilesDirectoryTree } from '../ui/filesDirectoryTree';
import {
  collectManifestNameMatches,
  expandFoldersForSearchHits,
  fillHighlightedText,
  mountFilesTreeSearchBar,
  pathAncestorKeys,
  stepSearchIndex,
  toSearchState,
  type FilesTreeSearchBarHandle,
  type FilesTreeSearchMatch,
} from '../ui/filesTreeSearch';
import { bindBrowseSplitResize } from '../ui/splitPaneResize';
import {
  bindFilesBrowseMobileTree,
  FILES_MOBILE_LAYOUT_MQ,
  type FilesBrowseMobileTreeHandle,
} from '../ui/filesBrowseMobile';
import { bindFilesDropUpload } from '../ui/filesDropUpload';
import { showFilesMoveDialog } from '../ui/filesMoveDialog';
import { showFilesPropertiesDialog } from '../ui/filesPropertiesDialog';
import { showConfirmDialog } from '../ui/confirmDialog';
import { fileTypeImg } from '../ui/fileTypeImg';
import { iconImg } from '../ui/icons';
import { invalidSharedFolderEntryName, showInputDialog, throwSharedFolderInputError } from '../ui/inputDialog';
import { recordFolderActivitySafe } from '../crypto/sharedFolderActivity';
import { renderSharedFolderAccessPanel } from '../ui/sharedFolderAccessPanel';
import { renderSharedFolderActivityPanel } from '../ui/sharedFolderActivityPanel';
import { renderSharedFolderDeletionHistoryPanel } from '../ui/sharedFolderDeletionHistoryPanel';
import { showFilePreview } from '../ui/filePreviewDialog';
import { showFileTextEditor, type FileTextEditorHandle } from '../ui/fileTextEditorDialog';
import { applyUploadProgress, hideUploadProgress } from '../ui/uploadProgress';
import {
  bindFilesConflictBanner,
  filesConflictBannerMarkup,
  formatFilesConflictStatus,
  hideFilesConflictBanner,
  showFilesConflictBanner,
} from '../ui/filesConflictBanner';
import { connectSharedFolderLive, disconnectSharedFolderLive } from '../ws/sharedFolderLive';
import {
  buildFilesFolderUrl,
  pushAppUrl,
  readFilesBrowsePath,
} from '../app/navigationState';

function entryDisplayName(path: string, currentPath: string): string {
  const prefix = currentPath ? `${currentPath}/` : '';
  return path.startsWith(prefix) ? path.slice(prefix.length) : path;
}

function deletionHistorySeenKey(userId: string, folderId: string): string {
  return `shareweb:deletionHistorySeen:${userId}:${folderId}`;
}

function readDeletionHistorySeen(userId: string, folderId: string): string | null {
  return localStorage.getItem(deletionHistorySeenKey(userId, folderId));
}

function markDeletionHistorySeen(userId: string, folderId: string): void {
  localStorage.setItem(deletionHistorySeenKey(userId, folderId), new Date().toISOString());
}

function entryNameConflict(
  manifest: SharedFolderManifestPlaintext,
  parentPath: string,
  newName: string,
  ignorePath?: string,
): string | null {
  const invalid = invalidSharedFolderEntryName(newName);
  if (invalid) {
    return invalid;
  }
  if (isManifestPathTaken(manifest, joinManifestPath(parentPath, newName), ignorePath)) {
    return t('files.owner.nameInUse');
  }
  return null;
}

function tFolderPermission(permission: string): string {
  switch (permission) {
    case 'Full':
      return t('files.owner.accessFull');
    case 'ReadWrite':
      return t('files.owner.accessReadWrite');
    case 'Read':
      return t('files.owner.accessRead');
    default:
      return permission;
  }
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export type FilesFolderDetailHandle = {
  dispose: () => void;
  switchFolder: (nextFolderId: string) => void;
};

export function mountFilesFolderDetail(host: HTMLElement, initialFolderId: string): FilesFolderDetailHandle {
  host.className = 'panel files-folder-panel files-browse-main files-workspace__detail';

  const detailEl = host;
  let folderId = initialFolderId;
  let disposed = false;
  let loadGeneration = 0;
  let folderDetail: SharedFolderDetail | null = null;
  let folderKey: Uint8Array | null = null;
  let manifest: SharedFolderManifestPlaintext = { entries: [] };
  let revision = 0;
  let currentPath = readFilesBrowsePath();
  let displayLabel: string | null = null;
  let busy = false;
  let liveConnected = false;
  let syncingManifest = false;
  let pendingConflictMessage = '';
  const expandedPaths = new Set<string>();
  let searchQuery = '';
  let searchMatches: FilesTreeSearchMatch[] = [];
  let searchIndex = -1;
  let searchHitEntryKey: string | null = null;
  let searchBar: FilesTreeSearchBarHandle | null = null;
  let searchBarBound = false;
  type DetailView = 'files' | 'access' | 'deletionHistory' | 'activity';
  let detailView: DetailView = 'files';
  let mountedView: DetailView | null = null;
  let disposePanel: (() => void) | null = null;
  let disposeDropUpload: (() => void) | null = null;
  let disposeBrowseSplit: (() => void) | null = null;
  let disposeMobileTree: (() => void) | null = null;
  let mobileTree: FilesBrowseMobileTreeHandle | null = null;
  let mountGeneration = 0;
  let chromeBound = false;
  let explorerActionsBound = false;
  let openTextEditor: FileTextEditorHandle | null = null;
  let deletionHistoryUnread = 0;
  let lastCommitActions: string | false = false;
  let lastChromeActions: string | false = false;
  let refreshListing: () => void = () => {};
  const explorerLayoutMq = typeof window !== 'undefined' ? window.matchMedia(FILES_MOBILE_LAYOUT_MQ) : null;
  const onExplorerLayoutChange = () => {
    explorerActionsBound = false;
    lastCommitActions = false;
    lastChromeActions = false;
    syncExplorerActions(true);
    syncChromeShareActions(true);
    patchDeletionHistoryBadge();
  };
  explorerLayoutMq?.addEventListener('change', onExplorerLayoutChange);

  const currentUserId = (): string => {
    const state = getAppState();
    return state.kind === 'authenticated' ? state.session.userId : '';
  };

  const refreshDeletionHistoryBadge = async (): Promise<void> => {
    if (!folderDetail?.canManageMembers || !folderKey) {
      deletionHistoryUnread = 0;
      return;
    }

    const userId = currentUserId();
    if (!userId) {
      deletionHistoryUnread = 0;
      return;
    }

    try {
      const deleted = await listDeletedSharedFolderFiles(folderId, folderKey, manifest);
      const seenAt = readDeletionHistorySeen(userId, folderId);
      deletionHistoryUnread = deleted.filter((item) => {
        const byOther = !(item.actorKind === 'user' && item.actorId === userId);
        const isNew = !seenAt || item.deletedAt > seenAt;
        return byOther && isNew;
      }).length;
    } catch (cause) {
      console.error('Deletion history badge refresh failed', cause);
    }
  };

  const patchDeletionHistoryBadge = (): void => {
    const badgeHost = (
      detailEl.querySelector('#files-folder-more')
      ?? detailEl.querySelector('#files-folder-deletion-history')
    ) as HTMLButtonElement | null;
    if (!badgeHost) {
      return;
    }

    badgeHost.classList.toggle('button--with-badge', deletionHistoryUnread > 0);
    const existing = badgeHost.querySelector('.button-badge');
    if (deletionHistoryUnread <= 0) {
      existing?.remove();
      return;
    }

    const label = deletionHistoryUnread > 99 ? '99+' : String(deletionHistoryUnread);
    const title = t('files.owner.deletionHistoryUnreadTitle', { count: deletionHistoryUnread });
    if (existing) {
      existing.textContent = label;
      existing.setAttribute('title', title);
      return;
    }

    const badge = document.createElement('span');
    badge.className = 'button-badge';
    badge.title = title;
    badge.textContent = label;
    badgeHost.append(badge);
  };

  const navigateToPath = (path: string, options?: { replace?: boolean }) => {
    mobileTree?.closeTree();
    const normalized = path.replace(/^\/+|\/+$/g, '');
    if (normalized === currentPath) {
      return;
    }
    currentPath = normalized;
    ensureExpandedAncestors(currentPath, expandedPaths);
    pushAppUrl(buildFilesFolderUrl(folderId, normalized), { replace: options?.replace });
    refreshListing();
  };

  const syncFromUrl = () => {
    const urlPath = readFilesBrowsePath();
    if (urlPath === currentPath) {
      return;
    }
    currentPath = urlPath;
    ensureExpandedAncestors(currentPath, expandedPaths);
    refreshListing();
  };

  const onBrowseRoute = () => syncFromUrl();
  host.addEventListener('files:browse', onBrowseRoute);
  host.closest('[data-files-workspace]')?.addEventListener('files:browse', onBrowseRoute);

  const actor = (): SharedFolderActorRef | undefined => {
    const state = getAppState();
    return state.kind === 'authenticated'
      ? { kind: 'user', id: state.session.userId, displayName: state.session.displayName }
      : undefined;
  };

  const setStatus = (key: string) => {
    const statusEl = detailEl.querySelector('#files-folder-status') as HTMLParagraphElement | null;
    const progressEl = detailEl.querySelector('#files-folder-progress') as HTMLProgressElement | null;
    const errorEl = detailEl.querySelector('#files-folder-error') as HTMLParagraphElement | null;
    if (statusEl) {
      statusEl.hidden = false;
      statusEl.textContent = t(key);
    }
    if (progressEl) {
      progressEl.hidden = true;
    }
    if (errorEl) {
      errorEl.hidden = true;
    }
  };

  const setError = (key: string) => {
    const statusEl = detailEl.querySelector('#files-folder-status') as HTMLParagraphElement | null;
    const progressEl = detailEl.querySelector('#files-folder-progress') as HTMLProgressElement | null;
    const errorEl = detailEl.querySelector('#files-folder-error') as HTMLParagraphElement | null;
    hideUploadProgress(statusEl, progressEl);
    if (errorEl) {
      errorEl.hidden = false;
      errorEl.textContent = t(key);
    }
  };

  const clearConflict = () => {
    pendingConflictMessage = '';
    hideFilesConflictBanner(detailEl.querySelector('#files-folder-conflict') as HTMLElement | null);
  };

  const showConflict = (message: string) => {
    pendingConflictMessage = message;
    const conflictEl = detailEl.querySelector('#files-folder-conflict') as HTMLElement | null;
    if (!conflictEl) {
      return;
    }
    bindFilesConflictBanner(conflictEl, clearConflict);
    showFilesConflictBanner(conflictEl, message);
  };

  const hideStatus = () => {
    const statusEl = detailEl.querySelector('#files-folder-status') as HTMLParagraphElement | null;
    const progressEl = detailEl.querySelector('#files-folder-progress') as HTMLProgressElement | null;
    hideUploadProgress(statusEl, progressEl);
  };

  const fileMime = (file: SharedFolderFileEntry): string =>
    file.contentType || inferContentTypeFromFileName(entryDisplayName(file.path, currentPath)) || 'application/octet-stream';

  const canPreviewFile = (file: SharedFolderFileEntry): boolean => {
    const fileName = entryDisplayName(file.path, currentPath);
    return resolveFilePreview(fileName, fileMime(file)).kind !== 'binary';
  };

  const canEditFile = (file: SharedFolderFileEntry): boolean =>
    Boolean(folderDetail?.canCommitManifest)
    && canEditPlainText(entryDisplayName(file.path, currentPath), fileMime(file), file.size);

  const openTextFileEditor = (file: SharedFolderFileEntry, text: string) => {
    if (!folderKey) {
      return;
    }

    openTextEditor?.close();
    const fileName = entryDisplayName(file.path, currentPath);
    openTextEditor = showFileTextEditor({
      fileName,
      fileId: file.fileId,
      initialText: text,
      entry: file,
      save: async (nextText) => {
        if (!folderKey) {
          throw new Error('status.error');
        }
        const result = await replaceSharedFolderFileContents({
          folderId,
          folderKey,
          revision,
          manifest,
          fileId: file.fileId,
          file: new Blob([nextText], { type: fileMime(file) }),
          actor: actor(),
        });
        manifest = result.manifest;
        revision = result.revision;
        return result.entry;
      },
      onClose: () => {
        openTextEditor = null;
      },
    });
  };

  const syncOpenTextEditor = async () => {
    const handle = openTextEditor;
    if (!handle?.isOpen() || !folderKey) {
      return;
    }

    const entry = manifest.entries.find(
      (item): item is SharedFolderFileEntry => item.type === 'file' && item.fileId === handle.fileId,
    );
    if (!entry) {
      handle.markDeleted();
      return;
    }
    if (!handle.needsRemote(entry)) {
      return;
    }

    const bytes = await downloadSharedFolderFile({ folderId, folderKey, entry });
    handle.applyRemote(new TextDecoder().decode(bytes), entry);
  };

  const openFile = async (file: SharedFolderFileEntry) => {
    if (!folderKey || busy) {
      return;
    }
    busy = true;
    setStatus('files.owner.downloading');
    try {
      const bytes = await downloadSharedFolderFile({ folderId, folderKey, entry: file });
      const fileName = entryDisplayName(file.path, currentPath);
      if (!canPreviewFile(file)) {
        triggerBrowserDownload(fileName, bytes);
      } else {
        const mime = fileMime(file);
        await showFilePreview(fileName, new Blob([toArrayBuffer(bytes)], { type: mime }), mime);
      }
      hideStatus();
    } catch {
      setError('status.error');
    } finally {
      busy = false;
    }
  };

  const editFile = async (file: SharedFolderFileEntry) => {
    if (!folderKey || busy) {
      return;
    }
    busy = true;
    setStatus('files.owner.downloading');
    try {
      const bytes = await downloadSharedFolderFile({ folderId, folderKey, entry: file });
      hideStatus();
      busy = false;
      openTextFileEditor(file, new TextDecoder().decode(bytes));
    } catch {
      setError('status.error');
      busy = false;
    }
  };

  const downloadFile = async (file: SharedFolderFileEntry) => {
    if (!folderKey || busy) {
      return;
    }
    busy = true;
    setStatus('files.owner.downloading');
    try {
      const bytes = await downloadSharedFolderFile({ folderId, folderKey, entry: file });
      triggerBrowserDownload(entryDisplayName(file.path, currentPath), bytes);
      hideStatus();
    } catch {
      setError('status.error');
    } finally {
      busy = false;
    }
  };

  const uploadFiles = async (files: File[], targetPath: string) => {
    if (!folderKey || busy || files.length === 0) {
      return;
    }
    busy = true;
    clearConflict();
    setStatus('files.owner.uploading');
    let failedKey: string | null = null;
    try {
      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
      let completedBytes = 0;
      for (const file of files) {
        if (disposed || !folderKey) {
          return;
        }
        const result = await uploadFileToSharedFolder({
          folderId,
          folderKey,
          revision,
          manifest,
          currentPath: targetPath,
          file,
          actor: actor(),
          onProgress: (progress) => {
            const statusEl = detailEl.querySelector('#files-folder-status') as HTMLParagraphElement | null;
            const progressEl = detailEl.querySelector('#files-folder-progress') as HTMLProgressElement | null;
            if (!statusEl) {
              return;
            }
            applyUploadProgress(statusEl, progressEl, {
              ...progress,
              bytesCompleted: completedBytes + progress.bytesCompleted,
              bytesTotal: totalBytes,
            });
          },
        });
        completedBytes += file.size;
        manifest = result.manifest;
        revision = result.revision;
        const who = actor();
        if (who && folderKey) {
          recordFolderActivitySafe({
            folderId,
            folderKey,
            entry: {
              actor: who,
              action: 'upload',
              path: joinManifestPath(targetPath, file.name),
            },
          });
        }
        const message = formatFilesConflictStatus(result.conflicts);
        if (message) {
          showConflict(message);
        }
      }
    } catch (cause) {
      failedKey = uploadErrorMessageKey(cause);
    } finally {
      busy = false;
    }
    if (disposed) {
      return;
    }
    refreshListing();
    if (failedKey) {
      setError(failedKey);
    } else {
      hideStatus();
    }
  };

  const setDetailView = (view: DetailView) => {
    if (view !== 'files' && detailView !== view && busy) {
      return;
    }
    detailView = detailView === view ? 'files' : view;
    render();
  };

  const onSubviewEscape = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || event.defaultPrevented || detailView === 'files') {
      return;
    }
    if (document.querySelector('.modal-backdrop')) {
      return;
    }
    event.preventDefault();
    detailView = 'files';
    render();
  };

  const ensureDetailShell = () => {
    if (detailEl.querySelector('#files-folder-main')) {
      return;
    }
    detailEl.innerHTML = `
      <div class="files-browse-chrome">
        <button type="button" class="icon-button files-browse-chrome__back" id="files-folder-back-list" data-i18n-aria-label="files.owner.backToList" data-i18n-title="files.owner.backToList"></button>
        <div class="files-browse-chrome__identity">
          <h2 class="files-browse-chrome__title" id="files-folder-title"></h2>
          <span class="files-browse-live" id="files-folder-live">${t('files.owner.liveConnected')}</span>
        </div>
        <div class="files-browse-chrome__share-actions"></div>
      </div>
      <div id="files-folder-main" class="files-folder-main"></div>
    `;
    const backBtn = detailEl.querySelector('#files-folder-back-list');
    backBtn?.prepend(iconImg('chevron-left', 16));
    chromeBound = false;
  };

  const openDeletionHistory = () => {
    const userId = currentUserId();
    if (userId) {
      markDeletionHistorySeen(userId, folderId);
      deletionHistoryUnread = 0;
      patchDeletionHistoryBadge();
    }
    setDetailView('deletionHistory');
  };

  const bindChromeOnce = () => {
    if (chromeBound) {
      return;
    }
    chromeBound = true;
    window.addEventListener('keydown', onSubviewEscape);
    detailEl.querySelector('#files-folder-back-list')?.addEventListener('click', () => {
      pushAppUrl('/files');
    });
    const actions = detailEl.querySelector('.files-browse-chrome__share-actions');
    actions?.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement | null)?.closest('button');
      if (!button || !actions.contains(button) || !folderKey) {
        return;
      }
      if (button.id === 'files-folder-more') {
        openFilesContextMenu({
          anchorRect: button.getBoundingClientRect(),
          items: [
            {
              id: 'manage',
              labelKey: 'files.owner.manageAccess',
              action: () => setDetailView('access'),
            },
            {
              id: 'deletion-history',
              labelKey: 'files.owner.deletionHistory',
              action: () => openDeletionHistory(),
            },
            {
              id: 'activity',
              labelKey: 'files.owner.activity',
              action: () => setDetailView('activity'),
            },
          ],
        });
        return;
      }
      if (button.id === 'files-folder-deletion-history') {
        openDeletionHistory();
        return;
      }
      if (button.id === 'files-folder-activity') {
        setDetailView('activity');
        return;
      }
      if (button.id === 'files-folder-manage') {
        setDetailView('access');
      }
    });
  };

  const syncChromeShareActions = (force = false) => {
    const actions = detailEl.querySelector('.files-browse-chrome__share-actions') as HTMLElement | null;
    if (!actions) {
      return;
    }
    const canManage = Boolean(folderDetail?.canManageMembers);
    const compact = window.matchMedia(FILES_MOBILE_LAYOUT_MQ).matches;
    const modeKey = canManage ? (compact ? 'compact' : 'full') : 'none';
    if (!force && modeKey === lastChromeActions) {
      return;
    }
    lastChromeActions = modeKey;

    if (!canManage) {
      actions.innerHTML = '';
      return;
    }

    if (compact) {
      actions.innerHTML = `<button type="button" class="icon-button button--with-badge" id="files-folder-more" data-i18n-aria-label="files.owner.moreActions" data-i18n-title="files.owner.moreActions" aria-haspopup="menu">⋯</button>`;
    } else {
      actions.innerHTML = `
        <button type="button" class="button ghost button--with-badge" id="files-folder-deletion-history" aria-pressed="false"><span data-i18n="files.owner.deletionHistory"></span></button>
        <button type="button" class="button ghost" id="files-folder-activity" aria-pressed="false"><span data-i18n="files.owner.activity"></span></button>
        <button type="button" class="button ghost" id="files-folder-manage" aria-pressed="false"><span data-i18n="files.owner.manageAccess"></span></button>
      `;
    }
    applyI18n(actions);
    patchDeletionHistoryBadge();
  };

  const setContentBusy = (isBusy: boolean) => {
    const tableWrap = detailEl.querySelector('.files-browse-files__table-wrap') as HTMLElement | null;
    const mainEl = detailEl.querySelector('#files-folder-main') as HTMLElement | null;
    if (tableWrap) {
      tableWrap.classList.toggle('files-browse-files__table-wrap--busy', isBusy);
      tableWrap.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }
    mainEl?.removeAttribute('aria-busy');
    mainEl?.classList.remove('files-folder-main--busy');
  };

  const showFilesListLoading = () => {
    const tbody = detailEl.querySelector('#files-folder-table-body') as HTMLTableSectionElement | null;
    const emptyEl = detailEl.querySelector('#files-folder-empty') as HTMLParagraphElement | null;
    const errorEl = detailEl.querySelector('#files-folder-error') as HTMLParagraphElement | null;
    const statusEl = detailEl.querySelector('#files-folder-status') as HTMLParagraphElement | null;
    const progressEl = detailEl.querySelector('#files-folder-progress') as HTMLProgressElement | null;
    if (emptyEl) {
      emptyEl.hidden = true;
    }
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    if (statusEl) {
      statusEl.hidden = true;
      statusEl.textContent = '';
    }
    if (progressEl) {
      progressEl.hidden = true;
    }
    clearConflict();
    if (!tbody) {
      return;
    }
    tbody.replaceChildren();
    const row = document.createElement('tr');
    row.className = 'files-table__loading-row';
    const cell = document.createElement('td');
    cell.colSpan = 4;
    cell.className = 'muted files-table__loading-cell';
    cell.textContent = t('status.loading');
    row.append(cell);
    tbody.append(row);
    setContentBusy(true);
  };

  const updateChrome = () => {
    ensureDetailShell();
    bindChromeOnce();
    if (!folderDetail) {
      return;
    }
    syncChromeShareActions();

    const shareTitle = displayLabel?.trim() || t('files.owner.untitledFolder');
    const titleEl = detailEl.querySelector('#files-folder-title') as HTMLHeadingElement | null;
    if (titleEl) {
      titleEl.textContent = shareTitle;
      titleEl.title = `${tInfoShareStatus(folderDetail.displayStatus)} · ${tFolderPermission(folderDetail.permission)}${syncingManifest ? ` · ${t('files.owner.syncingManifest')}` : ''}`;
    }
    const liveEl = detailEl.querySelector('#files-folder-live');
    liveEl?.classList.toggle('is-live', liveConnected);

    const historyBtn = detailEl.querySelector('#files-folder-deletion-history') as HTMLButtonElement | null;
    const activityBtn = detailEl.querySelector('#files-folder-activity') as HTMLButtonElement | null;
    const manageBtn = detailEl.querySelector('#files-folder-manage') as HTMLButtonElement | null;
    historyBtn?.setAttribute('aria-pressed', String(detailView === 'deletionHistory'));
    activityBtn?.setAttribute('aria-pressed', String(detailView === 'activity'));
    manageBtn?.setAttribute('aria-pressed', String(detailView === 'access'));
    patchDeletionHistoryBadge();
    applyI18n(detailEl.querySelector('.files-browse-chrome') ?? detailEl);
  };

  const ensureFilesExplorerShell = (mainEl: HTMLElement) => {
    if (mainEl.querySelector('.files-browse-split')) {
      return false;
    }
    mainEl.innerHTML = `
      <div class="files-browse-split">
        <div class="files-browse-tree-pane">
          <div id="files-folder-tree-search"></div>
          <nav id="files-folder-tree"></nav>
        </div>
        <section class="files-browse-files">
          <div class="files-explorer-bar">
            <nav class="files-folder-breadcrumb" id="files-folder-breadcrumb"></nav>
            <div class="files-explorer-bar__actions" id="files-folder-actions"></div>
          </div>
          <div class="files-table-wrap files-browse-files__table-wrap">
            <table class="data-table files-table">
              <thead>
                <tr>
                  <th data-i18n="files.owner.fileName"></th>
                  <th data-i18n="files.owner.propertiesType"></th>
                  <th data-i18n="files.owner.fileSize"></th>
                  <th class="data-table__actions-heading" data-i18n="files.owner.actions"></th>
                </tr>
              </thead>
              <tbody id="files-folder-table-body"></tbody>
            </table>
            <p class="muted files-browse-empty" id="files-folder-empty" hidden data-i18n="files.owner.emptyFolder"></p>
          </div>
          <p class="status-text" id="files-folder-status" hidden></p>
          <progress class="files-upload-progress" id="files-folder-progress" hidden></progress>
          ${filesConflictBannerMarkup('files-folder-conflict')}
          <p class="error-text" id="files-folder-error" hidden></p>
        </section>
      </div>
    `;
    applyI18n(mainEl);
    explorerActionsBound = false;
    searchBarBound = false;
    searchBar = null;
    lastCommitActions = false;
    disposeDropUpload?.();
    disposeDropUpload = null;
    disposeBrowseSplit?.();
    disposeMobileTree?.();
    mobileTree = null;
    const split = mainEl.querySelector('.files-browse-split') as HTMLElement | null;
    const explorerBar = mainEl.querySelector('.files-explorer-bar') as HTMLElement | null;
    disposeBrowseSplit = split ? bindBrowseSplitResize(split) : null;
    if (split && explorerBar) {
      mobileTree = bindFilesBrowseMobileTree({ root: split, explorerBar });
      disposeMobileTree = () => mobileTree?.dispose();
    } else {
      disposeMobileTree = null;
    }
    return true;
  };

  const renderSubviewShell = (mainEl: HTMLElement) => {
    const titleKey = detailView === 'access'
      ? 'files.owner.manageTitle'
      : detailView === 'activity'
        ? 'files.owner.activityTitle'
        : 'files.owner.deletionHistoryTitle';
    mainEl.innerHTML = `
      <div class="files-folder-subview">
        <div class="files-explorer-bar">
          <button type="button" class="button ghost files-folder-subview__back" id="files-folder-back-to-files" data-i18n-aria-label="files.owner.backToFiles" data-i18n-title="files.owner.backToFiles">
            <span class="files-folder-subview__back-icon" aria-hidden="true"></span>
            <span class="files-folder-subview__back-label" data-i18n="files.owner.backToFiles"></span>
          </button>
          <h3 class="files-folder-subview__title" data-i18n="${titleKey}"></h3>
        </div>
        <div id="files-folder-subview-body" class="files-folder-subview__body"></div>
      </div>
    `;
    mainEl.querySelector('.files-folder-subview__back-icon')?.append(iconImg('chevron-left', 16));
    applyI18n(mainEl);
    mainEl.querySelector('#files-folder-back-to-files')?.addEventListener('click', () => {
      detailView = 'files';
      render();
    });
  };

  const promptNewFolder = () => {
    if (!folderKey || busy) return;
    void showInputDialog({
      titleKey: 'files.owner.newFolderTitle',
      labelKey: 'files.owner.newFolderName',
      confirmLabelKey: 'files.owner.newFolderCreate',
      maxLength: 200,
      validate: (folderName) => entryNameConflict(manifest, currentPath, folderName),
      submit: async (folderName) => {
        if (!folderKey) {
          throwSharedFolderInputError(new Error('status.error'));
        }
        busy = true;
        clearConflict();
        try {
          const result = await createSharedFolderSubfolder({
            folderId,
            folderKey,
            revision,
            manifest,
            currentPath,
            folderName,
            actor: actor(),
          });
          manifest = result.manifest;
          revision = result.revision;
          const who = actor();
          if (who && folderKey) {
            recordFolderActivitySafe({
              folderId,
              folderKey,
              entry: {
                actor: who,
                action: 'create_directory',
                path: joinManifestPath(currentPath, folderName),
              },
            });
          }
          const message = formatFilesConflictStatus(result.conflicts);
          if (message) showConflict(message);
          refreshListing();
        } catch (cause) {
          throwSharedFolderInputError(cause);
        } finally {
          busy = false;
        }
      },
    });
  };

  const promptNewTextFile = () => {
    if (!folderKey || busy) return;
    void showInputDialog({
      titleKey: 'files.owner.newTextFileTitle',
      labelKey: 'files.owner.newTextFileName',
      confirmLabelKey: 'files.owner.newTextFileCreate',
      defaultValue: 'notes.txt',
      maxLength: 200,
      validate: (rawName) => {
        try {
          return entryNameConflict(manifest, currentPath, ensureTextFileExtension(rawName));
        } catch (cause) {
          return cause instanceof Error ? cause.message : t('files.owner.invalidName');
        }
      },
      submit: async (rawName) => {
        if (!folderKey) {
          throwSharedFolderInputError(new Error('status.error'));
        }
        const fileName = ensureTextFileExtension(rawName);
        busy = true;
        clearConflict();
        try {
          const result = await uploadFileToSharedFolder({
            folderId,
            folderKey,
            revision,
            manifest,
            currentPath,
            file: new File([], fileName, { type: 'text/plain' }),
            actor: actor(),
          });
          manifest = result.manifest;
          revision = result.revision;
          const path = joinManifestPath(currentPath, fileName);
          const created = result.manifest.entries.find(
            (entry): entry is SharedFolderFileEntry => entry.type === 'file' && entry.path === path,
          );
          refreshListing();
          if (created) {
            openTextFileEditor(created, '');
          }
        } catch (cause) {
          throwSharedFolderInputError(cause);
        } finally {
          busy = false;
        }
      },
    });
  };

  const triggerUploadPicker = () => {
    const uploadInput = detailEl.querySelector('#files-folder-upload') as HTMLInputElement | null;
    uploadInput?.click();
  };

  const isCompactExplorerActions = () => window.matchMedia(FILES_MOBILE_LAYOUT_MQ).matches;

  const syncExplorerActions = (force = false) => {
    if (!folderDetail) {
      return;
    }
    const actionsEl = detailEl.querySelector('#files-folder-actions') as HTMLElement | null;
    if (!actionsEl) {
      return;
    }
    const canCommit = Boolean(folderDetail.canCommitManifest);
    const compact = isCompactExplorerActions();
    const modeKey = canCommit ? (compact ? 'compact' : 'full') : 'none';
    if (!force && modeKey === lastCommitActions && explorerActionsBound) {
      return;
    }
    lastCommitActions = modeKey;
    if (!canCommit) {
      actionsEl.innerHTML = '';
      explorerActionsBound = false;
      bindExplorerActionsOnce();
      return;
    }

    if (compact) {
      actionsEl.innerHTML = `
        <button type="button" class="icon-button files-explorer-add" id="files-folder-add" data-i18n-aria-label="files.owner.addActions" data-i18n-title="files.owner.addActions" aria-haspopup="menu"></button>
        <input type="file" id="files-folder-upload" multiple hidden />
      `;
      actionsEl.querySelector('#files-folder-add')?.append(iconImg('plus', 16));
    } else {
      actionsEl.innerHTML = `
        <button type="button" class="button ghost" id="files-folder-new"><span data-i18n="files.owner.newFolder"></span></button>
        <button type="button" class="button ghost" id="files-folder-new-text"><span data-i18n="files.owner.newTextFile"></span></button>
        <label class="button primary files-folder-panel__upload"><span data-i18n="files.owner.upload"></span><input type="file" id="files-folder-upload" multiple hidden /></label>
      `;
    }
    applyI18n(actionsEl);
    explorerActionsBound = false;
    bindExplorerActionsOnce();
  };

  const bindExplorerActionsOnce = () => {
    if (explorerActionsBound) {
      return;
    }
    explorerActionsBound = true;

    detailEl.querySelector('#files-folder-add')?.addEventListener('click', (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      openFilesContextMenu({
        anchorRect: button.getBoundingClientRect(),
        items: [
          { id: 'new-folder', labelKey: 'files.owner.newFolder', action: () => promptNewFolder() },
          { id: 'new-text', labelKey: 'files.owner.newTextFile', action: () => promptNewTextFile() },
          { id: 'upload', labelKey: 'files.owner.upload', action: () => triggerUploadPicker() },
        ],
      });
    });

    detailEl.querySelector('#files-folder-new')?.addEventListener('click', () => promptNewFolder());
    detailEl.querySelector('#files-folder-new-text')?.addEventListener('click', () => promptNewTextFile());

    const uploadInput = detailEl.querySelector('#files-folder-upload') as HTMLInputElement | null;
    uploadInput?.addEventListener('change', () => {
      const files = Array.from(uploadInput.files ?? []);
      uploadInput.value = '';
      if (files.length === 0) {
        return;
      }
      void uploadFiles(files, currentPath);
    });

    if (!disposeDropUpload) {
      const splitEl = detailEl.querySelector('.files-browse-split') as HTMLElement | null;
      if (splitEl) {
        disposeDropUpload = bindFilesDropUpload({
          root: splitEl,
          getCurrentPath: () => currentPath,
          canUpload: () => Boolean(folderDetail?.canCommitManifest),
          isBusy: () => busy,
          onDrop: (dropped, targetPath) => {
            void uploadFiles(dropped, targetPath);
          },
          onRejectedEmpty: () => {
            setError('files.drop.emptyRejected');
          },
        });
      }
    }
  };

  const ensureFilesViewMounted = () => {
    ensureDetailShell();
    const mainEl = detailEl.querySelector('#files-folder-main') as HTMLElement;
    const needsShell = mountedView !== 'files' || !mainEl.querySelector('.files-browse-split');
    if (needsShell) {
      disposePanel?.();
      disposePanel = null;
      disposeDropUpload?.();
      disposeDropUpload = null;
      disposeBrowseSplit?.();
      disposeBrowseSplit = null;
      disposeMobileTree?.();
      disposeMobileTree = null;
      mobileTree = null;
      ensureFilesExplorerShell(mainEl);
      mountedView = 'files';
      mountGeneration += 1;
    }
    syncExplorerActions(needsShell);
  };

  const applySearchQuery = (query: string, resetIndex: boolean) => {
    searchQuery = query;
    searchMatches = collectManifestNameMatches(searchQuery, manifest);
    if (resetIndex) {
      searchIndex = -1;
      searchHitEntryKey = null;
    } else if (searchHitEntryKey) {
      searchIndex = searchMatches.findIndex((m) => m.entryKey === searchHitEntryKey);
      if (searchIndex < 0) searchHitEntryKey = null;
    }
    searchBar?.setResultCount(searchMatches.length);
    if (searchMatches.length > 0) {
      expandFoldersForSearchHits(
        searchMatches,
        (key) => expandedPaths.add(key),
        pathAncestorKeys,
      );
    }
  };

  const stepSearch = (direction: -1 | 1) => {
    if (searchMatches.length === 0) {
      applySearchQuery(searchQuery, true);
      if (searchMatches.length === 0) return;
    }
    searchIndex = stepSearchIndex(searchIndex, direction, searchMatches.length);
    const match = searchMatches[searchIndex];
    if (!match) return;
    searchHitEntryKey = match.entryKey;
    if (match.parentKey !== currentPath) {
      currentPath = match.parentKey;
      ensureExpandedAncestors(currentPath, expandedPaths);
      pushAppUrl(buildFilesFolderUrl(folderId, currentPath), { replace: true });
    }
    refreshListing();
  };

  const bindTreeSearchOnce = () => {
    if (searchBarBound) return;
    const searchHost = detailEl.querySelector('#files-folder-tree-search') as HTMLElement | null;
    if (!searchHost) return;
    searchBarBound = true;
    searchBar = mountFilesTreeSearchBar(searchHost, {
      placeholderKey: 'files.search.placeholder',
      initialQuery: searchQuery,
      onQueryChange: (query) => {
        applySearchQuery(query, true);
        refreshListing();
      },
      onStep: stepSearch,
    });
  };

  refreshListing = () => {
    if (disposed || !folderDetail || !folderKey || detailView !== 'files') {
      return;
    }

    closeFilesContextMenu();
    ensureFilesViewMounted();
    bindTreeSearchOnce();
    setContentBusy(false);
    applySearchQuery(searchQuery, false);
    const searchState = toSearchState(searchQuery, searchMatches);

    const treeHost = detailEl.querySelector('#files-folder-tree') as HTMLElement | null;
    const breadcrumbHost = detailEl.querySelector('#files-folder-breadcrumb') as HTMLElement | null;
    const tbody = detailEl.querySelector('#files-folder-table-body') as HTMLTableSectionElement | null;
    const emptyEl = detailEl.querySelector('#files-folder-empty') as HTMLParagraphElement | null;
    if (!treeHost || !breadcrumbHost || !tbody || !emptyEl) {
      return;
    }

    const paintTree = () => {
      renderFilesDirectoryTree(treeHost, manifest, {
        currentPath,
        expandedPaths,
        ariaLabelKey: 'files.owner.directoryTree',
        search: searchState,
        onSelect: (path) => {
          navigateToPath(path);
        },
        onToggle: (path, expanded) => {
          if (expanded) expandedPaths.add(path);
          else expandedPaths.delete(path);
          paintTree();
        },
      });
    };

    renderFilesBreadcrumb(breadcrumbHost, currentPath, (path) => {
      navigateToPath(path);
    });
    paintTree();

    tbody.replaceChildren();
    const { directories, files } = listFolderContents(manifest, currentPath);

    const openDirectory = (directory: SharedFolderDirectoryEntry) => {
      navigateToPath(directory.path);
    };

    const renameDirectory = async (directory: SharedFolderDirectoryEntry) => {
      if (!folderKey || busy) return;
      await showInputDialog({
        titleKey: 'files.owner.renameFolderTitle',
        labelKey: 'files.owner.renameFolderLabel',
        defaultValue: entryDisplayName(directory.path, currentPath),
        maxLength: 200,
        validate: (newName) => entryNameConflict(manifest, parentManifestPath(directory.path), newName, directory.path),
        submit: async (newName) => {
          if (!folderKey) {
            throwSharedFolderInputError(new Error('status.error'));
          }
          busy = true;
          clearConflict();
          try {
            const result = await renameSharedFolderSubfolder({ folderId, folderKey, revision, manifest, directoryPath: directory.path, newName });
            manifest = result.manifest;
            revision = result.revision;
            const who = actor();
            if (who && folderKey) {
              recordFolderActivitySafe({
                folderId,
                folderKey,
                entry: {
                  actor: who,
                  action: 'rename',
                  fromPath: directory.path,
                  toPath: joinManifestPath(parentManifestPath(directory.path), newName),
                  entryId: directory.entryId,
                },
              });
            }
            refreshListing();
          } catch (cause) {
            throwSharedFolderInputError(cause);
          } finally {
            busy = false;
          }
        },
      });
    };

    const deleteDirectory = async (directory: SharedFolderDirectoryEntry) => {
      if (!folderKey || busy) return;
      const recursive = !isDirectoryEmpty(manifest, directory.path);
      const confirmed = recursive
        ? await showConfirmDialog({
            titleKey: 'files.owner.deleteFolderTitle',
            message: t('files.owner.deleteFolderRecursiveConfirm', summarizeDirectoryDelete(manifest, directory.path)),
            confirmLabelKey: 'files.owner.delete',
            confirmClassName: 'primary',
          })
        : await showConfirmDialog({
            titleKey: 'files.owner.deleteFolderTitle',
            messageKey: 'files.owner.deleteFolderConfirm',
            confirmLabelKey: 'files.owner.delete',
            confirmClassName: 'primary',
          });
      if (!confirmed) return;
      busy = true;
      clearConflict();
      setStatus('files.owner.deleting');
      try {
        const result = await deleteSharedFolderSubfolder({ folderId, folderKey, revision, manifest, directoryPath: directory.path, recursive });
        manifest = result.manifest;
        revision = result.revision;
        const who = actor();
        if (who && folderKey) {
          recordFolderActivitySafe({
            folderId,
            folderKey,
            entry: {
              actor: who,
              action: 'delete',
              path: directory.path,
              entryId: directory.entryId,
            },
          });
        }
        if (currentPath === directory.path || currentPath.startsWith(`${directory.path}/`)) {
          navigateToPath('', { replace: true });
        } else {
          refreshListing();
        }
      } catch {
        setError('status.error');
      } finally {
        busy = false;
      }
    };

    const moveDirectory = async (directory: SharedFolderDirectoryEntry) => {
      if (!folderKey || busy) return;
      const destination = await showFilesMoveDialog({ manifest, currentPath, excludePath: directory.path });
      if (destination === null) return;
      busy = true;
      clearConflict();
      setStatus('files.owner.moving');
      try {
        const result = await moveSharedFolderSubfolder({
          folderId,
          folderKey,
          revision,
          manifest,
          directoryPath: directory.path,
          destinationDirectoryPath: destination,
        });
        manifest = result.manifest;
        revision = result.revision;
        const who = actor();
        const name = directory.path.split('/').pop() ?? directory.path;
        const toPath = destination ? `${destination}/${name}` : name;
        if (who && folderKey) {
          recordFolderActivitySafe({
            folderId,
            folderKey,
            entry: {
              actor: who,
              action: 'move',
              fromPath: directory.path,
              toPath,
              entryId: directory.entryId,
            },
          });
        }
        const message = formatFilesConflictStatus(result.conflicts);
        if (message) showConflict(message);
        if (currentPath === directory.path || currentPath.startsWith(`${directory.path}/`)) {
          navigateToPath(toPath, { replace: true });
        } else {
          refreshListing();
        }
      } catch {
        setError('status.error');
      } finally {
        busy = false;
      }
    };

    const renameFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || busy) return;
      await showInputDialog({
        titleKey: 'files.owner.renameFileTitle',
        labelKey: 'files.owner.renameFileLabel',
        defaultValue: entryDisplayName(file.path, currentPath),
        maxLength: 200,
        validate: (newName) => entryNameConflict(manifest, parentManifestPath(file.path), newName, file.path),
        submit: async (newName) => {
          if (!folderKey) {
            throwSharedFolderInputError(new Error('status.error'));
          }
          busy = true;
          clearConflict();
          try {
            const result = await renameSharedFolderFile({ folderId, folderKey, revision, manifest, filePath: file.path, newName });
            manifest = result.manifest;
            revision = result.revision;
            const who = actor();
            if (who && folderKey) {
              recordFolderActivitySafe({
                folderId,
                folderKey,
                entry: {
                  actor: who,
                  action: 'rename',
                  fromPath: file.path,
                  toPath: joinManifestPath(parentManifestPath(file.path), newName),
                  entryId: file.fileId,
                },
              });
            }
            refreshListing();
          } catch (cause) {
            throwSharedFolderInputError(cause);
          } finally {
            busy = false;
          }
        },
      });
    };

    const deleteFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || busy) return;
      const confirmed = await showConfirmDialog({
        titleKey: 'files.owner.deleteFileTitle',
        messageKey: 'files.owner.deleteFileConfirm',
        confirmLabelKey: 'files.owner.delete',
        confirmClassName: 'primary',
      });
      if (!confirmed) return;
      busy = true;
      clearConflict();
      setStatus('files.owner.deleting');
      try {
        const result = await deleteSharedFolderFile({ folderId, folderKey, revision, manifest, filePath: file.path });
        manifest = result.manifest;
        revision = result.revision;
        const who = actor();
        if (who && folderKey) {
          recordFolderActivitySafe({
            folderId,
            folderKey,
            entry: {
              actor: who,
              action: 'delete',
              path: file.path,
              entryId: file.fileId,
            },
          });
        }
        refreshListing();
      } catch {
        setError('status.error');
      } finally {
        busy = false;
      }
    };

    const moveFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || busy) return;
      const destination = await showFilesMoveDialog({ manifest, currentPath });
      if (destination === null) return;
      busy = true;
      clearConflict();
      setStatus('files.owner.moving');
      try {
        const result = await moveSharedFolderFile({
          folderId,
          folderKey,
          revision,
          manifest,
          filePath: file.path,
          destinationDirectoryPath: destination,
        });
        manifest = result.manifest;
        revision = result.revision;
        const who = actor();
        const name = file.path.split('/').pop() ?? file.path;
        const toPath = destination ? `${destination}/${name}` : name;
        if (who && folderKey) {
          recordFolderActivitySafe({
            folderId,
            folderKey,
            entry: {
              actor: who,
              action: 'move',
              fromPath: file.path,
              toPath,
              entryId: file.fileId,
            },
          });
        }
        const message = formatFilesConflictStatus(result.conflicts);
        if (message) showConflict(message);
        refreshListing();
      } catch {
        setError('status.error');
      } finally {
        busy = false;
      }
    };

    const openDirectoryMenu = (directory: SharedFolderDirectoryEntry, anchorRect: DOMRect) => {
      openFilesContextMenu({
        anchorRect,
        items: [
          { id: 'open', labelKey: 'files.owner.open', action: () => openDirectory(directory) },
          { id: 'rename', labelKey: 'files.owner.rename', disabled: !folderDetail!.canCommitManifest, action: () => { void renameDirectory(directory); } },
          { id: 'move', labelKey: 'files.owner.moveTo', disabled: !folderDetail!.canCommitManifest, action: () => { void moveDirectory(directory); } },
          { id: 'delete', labelKey: 'files.owner.delete', disabled: !folderDetail!.canCommitManifest, action: () => { void deleteDirectory(directory); } },
          { id: 'properties', labelKey: 'files.owner.properties', action: () => showFilesPropertiesDialog({ entry: directory, currentPath }) },
        ],
      });
    };

    const openFileMenu = (file: SharedFolderFileEntry, anchorRect: DOMRect) => {
      openFilesContextMenu({
        anchorRect,
        items: [
          { id: 'preview', labelKey: 'files.owner.preview', hidden: !canPreviewFile(file), action: () => { void openFile(file); } },
          { id: 'edit', labelKey: 'files.owner.edit', hidden: !canEditFile(file), action: () => { void editFile(file); } },
          { id: 'download', labelKey: 'files.owner.download', action: () => { void downloadFile(file); } },
          { id: 'rename', labelKey: 'files.owner.rename', disabled: !folderDetail!.canCommitManifest, action: () => { void renameFile(file); } },
          { id: 'move', labelKey: 'files.owner.moveTo', disabled: !folderDetail!.canCommitManifest, action: () => { void moveFile(file); } },
          { id: 'delete', labelKey: 'files.owner.delete', disabled: !folderDetail!.canCommitManifest, action: () => { void deleteFile(file); } },
          { id: 'properties', labelKey: 'files.owner.properties', action: () => showFilesPropertiesDialog({ entry: file, currentPath }) },
        ],
      });
    };

    const createNameButton = (icon: HTMLElement, label: string, onClick: () => void) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'files-table__name-button';
      const nameSpan = document.createElement('span');
      nameSpan.className = 'files-table__name-label';
      fillHighlightedText(nameSpan, label, searchQuery);
      button.append(icon, nameSpan);
      button.addEventListener('click', onClick);
      return button;
    };

    const createActionsCell = (
      visible: Array<{ labelKey: string; onClick: () => void }>,
      onMenu: (rect: DOMRect) => void,
    ) => {
      const actionsCell = document.createElement('td');
      actionsCell.className = 'data-table__actions';
      const wrap = document.createElement('div');
      wrap.className = 'data-table__actions-inner';
      for (const item of visible) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button ghost files-table__action-button';
        button.textContent = t(item.labelKey);
        button.addEventListener('click', item.onClick);
        wrap.append(button);
      }
      const menuButton = document.createElement('button');
      menuButton.type = 'button';
      menuButton.className = 'icon-button files-table__menu-button';
      menuButton.setAttribute('aria-label', t('files.owner.actions'));
      menuButton.textContent = '⋯';
      menuButton.addEventListener('click', () => onMenu(menuButton.getBoundingClientRect()));
      wrap.append(menuButton);
      actionsCell.append(wrap);
      return actionsCell;
    };

    for (const directory of directories) {
      const row = document.createElement('tr');
      if (searchHitEntryKey === directory.path) {
        row.classList.add('files-table__row--search-hit');
      }
      row.dataset.entryKey = directory.path;
      row.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        openDirectoryMenu(directory, new DOMRect(event.clientX, event.clientY, 0, 0));
      });

      const nameCell = document.createElement('td');
      nameCell.append(createNameButton(
        fileTypeImg('folder', 16),
        entryDisplayName(directory.path, currentPath),
        () => openDirectory(directory),
      ));

      const typeCell = document.createElement('td');
      typeCell.className = 'files-table__type';
      typeCell.textContent = t('files.owner.folderType');

      const sizeCell = document.createElement('td');
      sizeCell.textContent = '—';

      row.append(
        nameCell,
        typeCell,
        sizeCell,
        createActionsCell(
          [{ labelKey: 'files.owner.open', onClick: () => openDirectory(directory) }],
          (rect) => openDirectoryMenu(directory, rect),
        ),
      );
      tbody.append(row);
    }

    for (const file of files) {
      const row = document.createElement('tr');
      if (searchHitEntryKey === file.path) {
        row.classList.add('files-table__row--search-hit');
      }
      row.dataset.entryKey = file.path;
      row.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        openFileMenu(file, new DOMRect(event.clientX, event.clientY, 0, 0));
      });

      const fileName = entryDisplayName(file.path, currentPath);
      const nameCell = document.createElement('td');
      nameCell.append(createNameButton(
        fileTypeImg(fileTypeIconFor(fileName, file.contentType ?? inferContentTypeFromFileName(fileName)), 16),
        fileName,
        () => { void openFile(file); },
      ));

      const typeCell = document.createElement('td');
      typeCell.className = 'files-table__type';
      typeCell.textContent = t(fileTypeLabelKeyFor(fileName, file.contentType ?? inferContentTypeFromFileName(fileName)));

      const sizeCell = document.createElement('td');
      sizeCell.textContent = formatFileSize(file.size);

      const visibleActions = [
        ...(canPreviewFile(file)
          ? [{ labelKey: 'files.owner.preview', onClick: () => { void openFile(file); } }]
          : []),
        ...(canEditFile(file)
          ? [{ labelKey: 'files.owner.edit', onClick: () => { void editFile(file); } }]
          : []),
        { labelKey: 'files.owner.download', onClick: () => { void downloadFile(file); } },
      ];

      row.append(
        nameCell,
        typeCell,
        sizeCell,
        createActionsCell(visibleActions, (rect) => openFileMenu(file, rect)),
      );
      tbody.append(row);
    }

    emptyEl.hidden = directories.length > 0 || files.length > 0;
    if (searchHitEntryKey) {
      const hitRow = tbody.querySelector(`tr[data-entry-key="${CSS.escape(searchHitEntryKey)}"]`);
      hitRow?.scrollIntoView?.({ block: 'nearest' });
    }
    if (pendingConflictMessage) {
      showConflict(pendingConflictMessage);
    }
    applyI18n(detailEl.querySelector('.files-browse-files') ?? detailEl);
    const filesTable = detailEl.querySelector('.files-browse-files table.data-table') as HTMLTableElement | null;
    if (filesTable) {
      enhanceDataTableCards(filesTable);
    }
  };

  const render = () => {
    if (disposed || !folderDetail || !folderKey) {
      return;
    }

    closeFilesContextMenu();
    updateChrome();
    const mainEl = detailEl.querySelector('#files-folder-main') as HTMLElement;
    const activeFolderKey = folderKey;

    if (detailView !== 'files') {
      if (mountedView === detailView) {
        applyI18n(mainEl.querySelector('.files-explorer-bar') ?? mainEl);
        return;
      }

      disposePanel?.();
      disposePanel = null;
      disposeDropUpload?.();
      disposeDropUpload = null;
      explorerActionsBound = false;
      mountedView = detailView;
      const gen = ++mountGeneration;
      renderSubviewShell(mainEl);
      const body = mainEl.querySelector('#files-folder-subview-body') as HTMLElement;
      const view = detailView;
      void (async () => {
        const dispose = view === 'access'
          ? await renderSharedFolderAccessPanel(body, {
              folderId,
              folderKey: activeFolderKey,
              manifest,
              onQuickShareComplete: () => setDetailView('files'),
            })
          : view === 'activity'
            ? await renderSharedFolderActivityPanel(body, {
                folderId,
                folderKey: activeFolderKey,
              })
            : await renderSharedFolderDeletionHistoryPanel(body, {
                folderId,
                folderKey: activeFolderKey,
                revision,
                manifest,
                onReverted: (result) => {
                  manifest = result.manifest;
                  revision = result.revision;
                },
              });
        if (disposed || gen !== mountGeneration) {
          dispose();
          return;
        }
        disposePanel = dispose;
      })();
      return;
    }

    refreshListing();
  };

  const reloadManifestIfNewer = async (incomingRevision: number) => {
    if (disposed || !folderKey || busy || incomingRevision <= revision) {
      return;
    }
    syncingManifest = true;
    updateChrome();
    try {
      const loaded = await loadSharedFolderManifest(folderId, folderKey);
      if (disposed) {
        return;
      }
      if (loaded.revision > revision) {
        manifest = loaded.manifest;
        revision = loaded.revision;
        await refreshDeletionHistoryBadge();
      }
    } finally {
      syncingManifest = false;
      if (!disposed && folderDetail && folderKey) {
        updateChrome();
        refreshListing();
      }
    }
    if (!disposed) {
      void syncOpenTextEditor();
    }
  };

  const prepareSoftLoading = () => {
    ensureDetailShell();
    bindChromeOnce();
    const mainEl = detailEl.querySelector('#files-folder-main') as HTMLElement;
    disposePanel?.();
    disposePanel = null;

    // Leave chrome / tree / breadcrumb / toolbar alone when the explorer already exists.
    // Only rebuild the explorer if we are on a subview or have no shell yet.
    if (detailView !== 'files' || !mainEl.querySelector('.files-browse-split')) {
      detailView = 'files';
      mountedView = null;
      ensureFilesExplorerShell(mainEl);
      mountedView = 'files';
    }

    showFilesListLoading();
    const liveEl = detailEl.querySelector('#files-folder-live');
    liveEl?.classList.remove('is-live');
  };

  const resetFolderState = () => {
    openTextEditor?.close();
    openTextEditor = null;
    disposePanel?.();
    disposePanel = null;
    closeFilesContextMenu();
    disconnectSharedFolderLive();
    liveConnected = false;
    busy = false;
    syncingManifest = false;
    pendingConflictMessage = '';
    detailView = 'files';
    expandedPaths.clear();
    searchQuery = '';
    searchMatches = [];
    searchIndex = -1;
    searchHitEntryKey = null;
    searchBarBound = false;
    searchBar = null;
    deletionHistoryUnread = 0;
    // Keep previous chrome chrome/title visible until the next load paints;
    // only clear crypto/content state so actions no-op during the switch.
    folderKey = null;
    manifest = { entries: [] };
    revision = 0;
    currentPath = readFilesBrowsePath();
    ensureExpandedAncestors(currentPath, expandedPaths);
  };

  const load = async (generation: number) => {
    const session = loadVaultSession();
    if (!session) {
      if (disposed || generation !== loadGeneration) {
        return;
      }
      detailEl.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
      applyI18n(detailEl);
      return;
    }

    const nextDetail = await api.getSharedFolder(folderId);
    if (disposed || generation !== loadGeneration) {
      return;
    }
    folderDetail = nextDetail;
    displayLabel = null;
    folderKey = await unwrapSharedFolderKey(folderDetail.wrappedFolderKey, folderDetail.id, session.userPrivateKey);
    if (disposed || generation !== loadGeneration) {
      return;
    }
    if (folderDetail.labelBlobId) {
      try {
        displayLabel = await resolveFolderShareLabel(
          folderDetail.id,
          folderDetail.wrappedFolderKey,
          folderDetail.labelBlobId,
        );
      } catch {
        // Label is cosmetic — keep untitled fallback and still load the manifest.
        displayLabel = null;
      }
    }
    if (disposed || generation !== loadGeneration) {
      return;
    }

    const loaded = await loadSharedFolderManifest(folderId, folderKey);
    if (disposed || generation !== loadGeneration) {
      return;
    }
    manifest = loaded.manifest;
    revision = loaded.revision;
    await refreshDeletionHistoryBadge();
    if (disposed || generation !== loadGeneration) {
      return;
    }
    setContentBusy(false);
    hideStatus();
    updateChrome();
    refreshListing();

    connectSharedFolderLive(folderId, {
      onManifestRevision: (incomingRevision) => {
        void reloadManifestIfNewer(incomingRevision);
      },
      onStateChange: (state) => {
        if (disposed || generation !== loadGeneration) {
          return;
        }
        liveConnected = state === 'connected';
        const liveEl = detailEl.querySelector('#files-folder-live');
        liveEl?.classList.toggle('is-live', liveConnected);
      },
    });
  };

  const startLoad = () => {
    const generation = ++loadGeneration;
    prepareSoftLoading();
    void load(generation).catch(() => {
      if (disposed || generation !== loadGeneration) {
        return;
      }
      detailEl.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
      applyI18n(detailEl);
    });
  };

  const switchFolder = (nextFolderId: string) => {
    if (disposed) {
      return;
    }
    if (nextFolderId === folderId && folderDetail && folderKey) {
      currentPath = readFilesBrowsePath();
      refreshListing();
      return;
    }
    folderId = nextFolderId;
    resetFolderState();
    startLoad();
  };

  const unbindLocale = bindLocaleRefresh(() => {
    if (folderDetail && folderKey) {
      updateChrome();
      refreshListing();
    }
  });

  const dispose = () => {
    if (disposed) {
      return;
    }
    disposed = true;
    loadGeneration += 1;
    unbindLocale();
    host.removeEventListener('files:browse', onBrowseRoute);
    host.closest('[data-files-workspace]')?.removeEventListener('files:browse', onBrowseRoute);
    openTextEditor?.close();
    openTextEditor = null;
    disposePanel?.();
    disposePanel = null;
    disposeDropUpload?.();
    disposeDropUpload = null;
    disposeBrowseSplit?.();
    disposeBrowseSplit = null;
    disposeMobileTree?.();
    disposeMobileTree = null;
    mobileTree = null;
    explorerLayoutMq?.removeEventListener('change', onExplorerLayoutChange);
    window.removeEventListener('keydown', onSubviewEscape);
    closeFilesContextMenu();
    disconnectSharedFolderLive();
  };

  startLoad();

  return { dispose, switchFolder };
}
